import { Component, DoCheck, Input } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { CARBON_SCOPE, EmissionFactor, getCarbonScopeIcon, getUnitsForEmissionFactor, Organisation } from '../../common/organisation';
import { USER_ROLE } from '../../common/user';
import { FUNCTION, FunctionService } from '../../model-services/function.service';
import { Column } from '../../ui-components/table/table-component';

const SCOPE_ORDER = [CARBON_SCOPE.ScopeOne, CARBON_SCOPE.ScopeTwo, CARBON_SCOPE.ScopeThree]

// what the table shows for a factor; actions look the factor up again by _key
interface FactorRow {
    _key:string
    icon:string
    scope:string
    name:string
    description:string
    type:string
    factor:string
    usage:string
    canEdit:boolean
    canDelete:boolean
}

@Component({
    selector: 'app-emission-factors',
    templateUrl: 'emission-factors.html',
    standalone: false
})
export class EmissionFactorsComponent implements DoCheck {
    @Input() organisation: Organisation

    columns:Column[] = []
    data:FactorRow[] = []

    // factors are saved as a new list, so watch for one
    private shownFactors:EmissionFactor[]
    private shownUsageSource:any
    private decimal = new DecimalPipe('en')

    constructor(private functionService:FunctionService) {
    }

    ngDoCheck() {
        if (this.organisation && (this.organisation.emissionFactors !== this.shownFactors || this.functionService.emissionUsageLinqset !== this.shownUsageSource)) this.refresh()
    }

    private refresh() {
        this.shownFactors = this.organisation.emissionFactors
        this.shownUsageSource = this.functionService.emissionUsageLinqset
        const admin = this.isAdmin()

        this.data = (this.shownFactors || [])
            .map(ef => {
                const used = this.shownUsageSource ? this.functionService.emissionFactorUsage(ef._key).length : undefined
                return {
                    _key: ef._key,
                    icon: getCarbonScopeIcon(ef.scope),
                    scope: ef.scope,
                    name: ef.name,
                    description: ef.description,
                    type: ef.type ? ef.type.charAt(0).toUpperCase() + ef.type.slice(1) : '',
                    factor: `${this.decimal.transform(ef.value || 0, '1.0-4')} kg CO₂e per ${getUnitsForEmissionFactor(ef.type) || 'unit'}`,
                    usage: used === undefined ? '' : used ? `${used} infrastructure` : 'Not used',
                    canEdit: admin,
                    canDelete: admin,
                }
            })
            // by scope, then name
            .sort((a, b) => SCOPE_ORDER.indexOf(a.scope as CARBON_SCOPE) - SCOPE_ORDER.indexOf(b.scope as CARBON_SCOPE) || (a.name || '').localeCompare(b.name || ''))

        this.columns = [
            new Column('icon', '', 'icon'),
            new Column('scope', 'Scope', 'string'),
            new Column('name', 'Name', 'string'),
            new Column('description', 'Description', 'string'),
            new Column('type', 'Type', 'string'),
            new Column('factor', 'Factor', 'string'),
        ]
        // usage is only known once the organisation model has loaded
        if (this.shownUsageSource) this.columns.push(new Column('usage', 'Used by', 'string'))
        if (admin) {
            this.columns.push(new Column('canEdit', 'Edit', 'icon-button', 'Edit this factor', 'accent', 'edit'))
            this.columns.push(new Column('canDelete', 'Delete', 'icon-button', 'Delete this factor', 'warn', 'delete'))
        }
    }

    isAdmin() {
        return this.organisation?.role === USER_ROLE.ADMIN
    }

    async handleRowEvent(rowEvent:{row:any, column?:Column}) {
        const factor = rowEvent.row && this.organisation.emissionFactors?.find(ef => ef._key === rowEvent.row._key)
        if (!factor || !rowEvent.column || !this.isAdmin()) return
        if (rowEvent.column.name === "canDelete") this.functionService.executeFunction(FUNCTION.DELETE_EMISSION_FACTOR, factor)
        else if (rowEvent.column.name === "canEdit") this.functionService.executeFunction(FUNCTION.MAINTAIN_EMISSION_FACTOR, factor)
    }
}
